<template>
  <RfDialog ref="dialogRef" title="Audit Details" width="1200" hideConfirm>
    <div class="max-h-[70vh] overflow-auto">
      <div class="mb-6">
        <div class="mb-3">
          <b>Entity:</b> {{ audit.auditable_type }} -
          {{ audit.auditable_name || audit.auditable_id }}
        </div>
        <div class="mb-3">
          <b>Action:</b>
          <RfColorTag
            :backgroundColor="actionColor"
            :title="audit.action"
            size="small"
            pill
            disabled
            class="ml-2"
          />
        </div>
        <div class="mb-3"><b>User:</b> {{ audit.user_name }} ({{ audit.user_email }})</div>
        <div class="mb-3"><b>Timestamp:</b> {{ formattedDate }}</div>
        <div class="mb-3"><b>Request UUID:</b> {{ audit.request_uuid }}</div>
        <div class="mb-3"><b>IP Address:</b> {{ audit.remote_address }}</div>
      </div>

      <div class="mb-2">
        <b>Changes:</b>
      </div>
      <table class="w-full border border-gray-300 bg-white text-sm">
        <thead>
          <tr class="bg-gray-100">
            <th class="border border-gray-300 px-4 py-2 text-left">Field</th>
            <th class="border border-gray-300 px-4 py-2 text-left">Before</th>
            <th class="border border-gray-300 px-4 py-2 text-left">After</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(change, field) in parsedChanges" :key="field">
            <td class="border border-gray-300 px-4 py-2 align-top font-medium">{{ field }}</td>

            <template v-if="change.keyDiff">
              <td colspan="2" class="border border-gray-300 px-2 py-2 align-top">
                <div class="mb-2 flex items-center gap-2 text-xs">
                  <div class="flex overflow-hidden rounded border border-gray-300">
                    <button
                      type="button"
                      class="px-2 py-1"
                      :class="
                        viewMode(field) === 'keys'
                          ? 'bg-gray-200 font-medium'
                          : 'bg-white hover:bg-gray-50'
                      "
                      :aria-pressed="viewMode(field) === 'keys'"
                      @click="setViewMode(field, 'keys')"
                    >
                      Key diff
                    </button>
                    <button
                      type="button"
                      class="px-2 py-1"
                      :class="
                        viewMode(field) === 'json'
                          ? 'bg-gray-200 font-medium'
                          : 'bg-white hover:bg-gray-50'
                      "
                      :aria-pressed="viewMode(field) === 'json'"
                      @click="setViewMode(field, 'json')"
                    >
                      Full JSON
                    </button>
                  </div>
                  <button
                    v-if="unchangedCount(change) > 0"
                    type="button"
                    class="text-blue-600 underline"
                    :aria-expanded="!!showUnchanged[field]"
                    @click="toggleUnchanged(field)"
                  >
                    {{ showUnchanged[field] ? "Hide" : "Show" }}
                    {{ unchangedCount(change) }} unchanged
                    {{ unchangedCount(change) === 1 ? "key" : "keys" }}
                  </button>
                </div>

                <table v-if="viewMode(field) === 'keys'" class="w-full text-xs">
                  <thead>
                    <tr class="bg-gray-50">
                      <th class="border border-gray-200 px-2 py-1 text-left">Key</th>
                      <th class="border border-gray-200 px-2 py-1 text-left">Status</th>
                      <th class="border border-gray-200 px-2 py-1 text-left">Before</th>
                      <th class="border border-gray-200 px-2 py-1 text-left">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="entry in visibleEntries(field, change)"
                      :key="entry.key"
                      :class="entry.status === 'unchanged' ? 'text-gray-400' : ''"
                    >
                      <td class="border border-gray-200 px-2 py-1 font-medium">{{ entry.key }}</td>
                      <td class="border border-gray-200 px-2 py-1">
                        <RfColorTag
                          :backgroundColor="statusColor(entry.status)"
                          :title="entry.status"
                          size="small"
                          pill
                          disabled
                        />
                      </td>
                      <td
                        class="border border-gray-200 px-2 py-1 align-top break-all whitespace-pre-wrap"
                      >
                        {{ entryDisplay(entry.before) }}
                      </td>
                      <td
                        class="border border-gray-200 px-2 py-1 align-top break-all whitespace-pre-wrap"
                      >
                        {{ entryDisplay(entry.after) }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div v-else class="grid grid-cols-2 gap-2">
                  <component
                    :is="codeEditor.CodeEditor"
                    v-bind="{ ...codeEditor.props, languages: codeEditor.languages('json') }"
                    :modelValue="formatValue(change.before)"
                    readOnly
                    class="text-xs"
                  />
                  <component
                    :is="codeEditor.CodeEditor"
                    v-bind="{ ...codeEditor.props, languages: codeEditor.languages('json') }"
                    :modelValue="formatValue(change.after)"
                    readOnly
                    class="text-xs"
                  />
                </div>
              </td>
            </template>

            <template v-else>
              <td class="border border-gray-300 px-2 py-2 align-top">
                <component
                  :is="codeEditor.CodeEditor"
                  v-if="change.before !== null && change.before !== undefined"
                  v-bind="{ ...codeEditor.props, languages: codeEditor.languages('json') }"
                  :modelValue="formatValue(change.before)"
                  readOnly
                  class="text-xs"
                />
                <span v-else class="text-gray-500 italic">(empty)</span>
              </td>
              <td class="border border-gray-300 px-2 py-2 align-top">
                <component
                  :is="codeEditor.CodeEditor"
                  v-if="change.after !== null && change.after !== undefined"
                  v-bind="{ ...codeEditor.props, languages: codeEditor.languages('json') }"
                  :modelValue="formatValue(change.after)"
                  readOnly
                  class="text-xs"
                />
                <span v-else class="text-gray-500 italic">(empty)</span>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <template #footer>
      <RfButtonNew type="tertiary" @click="close">Close</RfButtonNew>
    </template>
  </RfDialog>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import dayjs from "dayjs";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import { useCodeEditor } from "@/utils/composables/useCodeEditor";
import { diffObjectKeys, isKeyDiffablePair, toChangePair } from "@/utils/jsonDiffUtils";

const codeEditor = useCodeEditor();

const props = defineProps({
  audit: {
    type: Object,
    required: true,
  },
});

const dialogRef = ref();

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

defineExpose({ open, close });

const formattedDate = computed(() => {
  return dayjs(props.audit.created_at).format("MMM DD, YYYY h:mm A");
});

const actionColor = computed(() => {
  switch (props.audit.action) {
    case "create":
      return "#DFF1BE"; // Light green for create
    case "update":
      return "#E0EDF9"; // Light blue for update
    case "destroy":
      return "#FFDDEF"; // Light pink for destroy
    default:
      return "#EDEBE4"; // Light gray for default
  }
});

const parsedChanges = computed(() => {
  const changes = {};
  const auditedChanges = props.audit.audited_changes || {};

  for (const [field, values] of Object.entries(auditedChanges)) {
    const [before, after] = toChangePair(values);

    if (isKeyDiffablePair(before, after)) {
      changes[field] = { before, after, keyDiff: diffObjectKeys(before, after) };
    } else {
      changes[field] = { before, after };
    }
  }

  return changes;
});

const viewModes = reactive({});
const showUnchanged = reactive({});

// The modal stays mounted between audits, so per-field toggles must reset when the
// displayed audit changes — every audit opens on defaults.
watch(
  () => props.audit,
  () => {
    Object.keys(viewModes).forEach(key => delete viewModes[key]);
    Object.keys(showUnchanged).forEach(key => delete showUnchanged[key]);
  },
);

const viewMode = field => viewModes[field] ?? "keys";

const setViewMode = (field, mode) => {
  viewModes[field] = mode;
};

const toggleUnchanged = field => {
  showUnchanged[field] = !showUnchanged[field];
};

const unchangedCount = change =>
  change.keyDiff.filter(entry => entry.status === "unchanged").length;

const orderedEntries = change => [
  ...change.keyDiff.filter(entry => entry.status !== "unchanged"),
  ...change.keyDiff.filter(entry => entry.status === "unchanged"),
];

const visibleEntries = (field, change) =>
  showUnchanged[field]
    ? orderedEntries(change)
    : orderedEntries(change).filter(entry => entry.status !== "unchanged");

const statusColor = status => {
  switch (status) {
    case "changed":
      return "#E0EDF9"; // Light blue for changed
    case "added":
      return "#DFF1BE"; // Light green for added
    case "removed":
      return "#FFDDEF"; // Light pink for removed
    default:
      return "#EDEBE4"; // Light gray for unchanged
  }
};

const entryDisplay = value => {
  if (value === undefined) return "(none)";
  return formatValue(value);
};

const formatValue = value => {
  if (value === null || value === undefined) {
    return "(empty)";
  }

  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  // For primitive values, return as JSON string
  return JSON.stringify(value);
};
</script>
